// ----------------------------------------------------------------------------
// DeveloperApiView — mock developer documentation page.
// Showcases a stylized endpoint reference and a JSON response code block.
// ----------------------------------------------------------------------------

import React, { useState } from "react";
import { Copy, Check, Terminal, KeyRound, Zap, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { API_VIEW } from "../../constants/testIds";

const SAMPLE_JSON = {
  status: "ok",
  endpoint: "GET /api/v1/bids",
  count: 5,
  data: [
    {
      id: "RFQ-4-26-09",
      title: "Minor Construction & Commercial Repairs",
      agency: "Brevard County BoCC",
      platform: "VendorLink",
      location: "Countywide / Melbourne",
      closing: "2026-06-15",
      type: "Small Business Set-Aside",
      value_tier: "Under $200k",
      status: "active",
    },
    {
      id: "33-ITB-26",
      title: "Pond & Drainage Improvements (Cadillac Dr & RJ Conlan)",
      agency: "City of Palm Bay",
      platform: "BonfireHub",
      location: "Palm Bay",
      closing: "2026-06-09",
      type: "Municipal ITB",
      value_tier: "Mid-Tier",
      status: "active",
    },
  ],
};

const ENDPOINTS = [
  { method: "GET", path: "/api/v1/bids", desc: "List active bids (filters supported)." },
  { method: "GET", path: "/api/v1/bids/:id", desc: "Retrieve full bid metadata." },
  { method: "POST", path: "/api/v1/bids/:id/save", desc: "Bookmark to Saved Contracts." },
  { method: "GET", path: "/api/v1/alerts", desc: "Read alert preference flags." },
  { method: "PATCH", path: "/api/v1/alerts", desc: "Update alert preference flags." },
];

const methodColor = {
  GET: "bg-emerald-100 text-emerald-700 border-emerald-200",
  POST: "bg-sky-100 text-sky-700 border-sky-200",
  PATCH: "bg-amber-100 text-amber-800 border-amber-200",
  DELETE: "bg-rose-100 text-rose-700 border-rose-200",
};

export const DeveloperApiView = () => {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(SAMPLE_JSON, null, 2);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      toast.success("Sample payload copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-sky-700 font-semibold">
          For Engineers · v1
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
          Developer API
        </h1>
        <p className="text-slate-500 text-sm sm:text-base max-w-2xl">
          Programmatic access to the Space Coast Procurement Portal. REST,
          JSON-only, Bearer-token auth.
        </p>
      </header>

      {/* Quickstart strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          {
            icon: KeyRound,
            label: "Authentication",
            body: "Bearer tokens scoped per vendor org. Rotate from Settings → Tokens.",
          },
          {
            icon: Zap,
            label: "Rate Limits",
            body: "120 requests / minute · burst 30. 429 includes Retry-After header.",
          },
          {
            icon: BookOpen,
            label: "Versioning",
            body: "All endpoints namespaced under /api/v1. Breaking changes versioned.",
          },
        ].map((card) => (
          <div
            key={card.label}
            className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-2">
              <card.icon size={15} strokeWidth={1.7} className="text-sky-600" />
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">
                {card.label}
              </p>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>

      {/* Endpoint table */}
      <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center gap-2">
          <Terminal size={16} strokeWidth={1.7} className="text-slate-600" />
          <h2 className="font-display text-lg font-semibold text-slate-900">
            Endpoints
          </h2>
        </div>
        <div className="divide-y divide-slate-100">
          {ENDPOINTS.map((e) => (
            <div
              key={e.path + e.method}
              className="px-6 py-3.5 flex items-center gap-4 hover:bg-slate-50/60 transition-colors"
            >
              <span
                className={`inline-flex items-center justify-center min-w-[60px] px-2 py-0.5 rounded text-[11px] font-semibold border ${methodColor[e.method]}`}
              >
                {e.method}
              </span>
              <code className="font-mono text-[13px] text-slate-800">{e.path}</code>
              <span className="text-sm text-slate-500 ml-auto hidden sm:inline">
                {e.desc}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* JSON sample */}
      <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">
              Sample Response
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              <code className="font-mono text-[12px]">GET /api/v1/bids</code> · 200 OK
            </p>
          </div>
          <button
            type="button"
            data-testid={API_VIEW.copy}
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors"
          >
            {copied ? (
              <>
                <Check size={13} strokeWidth={2} /> Copied
              </>
            ) : (
              <>
                <Copy size={13} strokeWidth={1.8} /> Copy JSON
              </>
            )}
          </button>
        </div>
        <pre className="font-mono text-[12.5px] leading-relaxed bg-[#0f172a] text-slate-200 px-6 py-5 overflow-x-auto thin-scroll">
          {json}
        </pre>
      </section>
    </div>
  );
};
